import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { setToken } from "../lib/nativeTokenStorage";
import { readWebAttribution } from "../lib/webAttribution";
import { startGoogleLogin } from "../lib/googleAuth";
import { isNative } from "../lib/nativePlatform";

/**
 * P0 2026-10-01 — Gully-owned Google OAuth callback (no Emergent).
 *
 * Google redirects here after the user authenticates:
 *   /api/auth/google/callback?code=<auth-code>&state=<signed-state>
 * (this path is the pre-registered Google redirect URI; askgully.com
 * serves the SPA shell for it since the /api proxy was removed).
 *
 * This component POSTs {code, state} to /auth/google/exchange, persists
 * the returned JWT via the existing setToken() path (same mechanism the
 * email-login flow uses), sets the user, and navigates to the
 * backend-validated return_to.
 *
 * XX.285j — Native OAuth: backend returns {ticket} instead of {token}.
 * Deep-link out to the app; the app redeems the ticket for the JWT.
 * This SPA is running inside a Custom Tab / SFSafariViewController.
 *
 * 2026-10-11 (Option A, release-candidate/oauth-return-fallback-20261011):
 * SFSafariViewController silently ignores programmatic window.location.href
 * to custom schemes. After a successful ticket exchange, we still attempt
 * the automatic deep-link, but if the browser hasn't handed off after
 * HANDOFF_GRACE_MS, we show a user-tappable "Return to Gully" link.
 * The ticket-bearing link is ONLY rendered after a successful exchange —
 * never on failure, timeout, or incomplete state.
 *
 * Safety properties:
 * - Bounded EXCHANGE_TIMEOUT_MS on the /exchange POST (AbortController).
 * - "Signing you in…" can never persist indefinitely; timeout and errors
 *   transition to an error/retry state.
 * - Duplicate exchanges are prevented (processed ref + requestId guard).
 * - Stale/late responses after unmount, timeout, or retry are ignored.
 * - The ticket is never written to console, analytics, or error messages.
 *   It appears only in the deep-link href (required for the handoff).
 * - code/state query params are stripped from the URL via replaceState
 *   as soon as they are read, so they don't linger in history.
 */

// Bounded network timeout for the exchange POST.
const EXCHANGE_TIMEOUT_MS = 15000;
// Grace period after ticket receipt before showing the manual fallback.
// The automatic window.location.href attempt runs immediately; if the
// OS hasn't switched apps by now, SFSafariViewController likely ignored it.
const HANDOFF_GRACE_MS = 2000;

const NATIVE_SCHEME = "com.gullyapp.mobile://oauth/callback";

function buildDeepLink(ticket) {
  return `${NATIVE_SCHEME}?ticket=${encodeURIComponent(ticket)}`;
}

export default function GoogleOAuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  // Monotonic request id: stale/late responses from a previous attempt
  // (after timeout, retry, or unmount) are ignored.
  const requestId = useRef(0);

  // phase: 'loading' | 'handoff' | 'error'
  const [phase, setPhase] = useState("loading");
  // Ticket is set ONLY after a successful exchange that returned one.
  // The fallback link reads from this state; it is never populated on
  // failure, so a ticket-bearing link cannot render in an error state.
  const [ticket, setTicket] = useState(null);
  const [showFallback, setShowFallback] = useState(false);
  // error: null | 'missing_params' | 'timeout' | 'exchange_failed'
  // These are UI codes only — they never carry the ticket or raw errors.
  const [error, setError] = useState(null);

  const runExchange = useCallback(async () => {
    const myRequest = ++requestId.current;
    setPhase("loading");
    setTicket(null);
    setShowFallback(false);
    setError(null);

    const params = new URLSearchParams(window.location.search || "");
    const code = params.get("code");
    const state = params.get("state");

    // Strip code/state from the visible URL immediately so one-time
    // credentials don't linger in history or get copied.
    try {
      window.history.replaceState(null, "", window.location.pathname);
    } catch {
      /* non-fatal */
    }

    if (!code || !state) {
      if (requestId.current === myRequest) {
        setPhase("error");
        setError("missing_params");
      }
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
    let handoffTimer = null;

    try {
      // Iter XX.285d — Attach the latest known web attribution to the
      // Google exchange payload so Meta campaign/ad/creative survives
      // Google OAuth registration. Non-blocking: read errors → omit.
      let _webAttribution = null;
      try {
        _webAttribution = readWebAttribution();
      } catch {
        /* fail-soft */
      }
      // Iter XX.285i — Anonymous session continuity (Claim Your Cricket).
      let _sessionId = null;
      try {
        _sessionId = localStorage.getItem("gully_web_session_id");
      } catch {
        /* fail-soft */
      }
      const { data } = await api.post(
        "/auth/google/exchange",
        {
          code,
          state,
          ...(_webAttribution ? { web_attribution: _webAttribution } : {}),
          ...(_sessionId ? { session_id: _sessionId } : {}),
        },
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);

      // Ignore stale responses: a retry, timeout, or unmount may have
      // superseded this request.
      if (requestId.current !== myRequest) return;

      // XX.285j — Native OAuth: backend returns {ticket} instead of {token}.
      if (data.ticket) {
        // Requirement: ticket state is set ONLY here, on success.
        setTicket(data.ticket);
        setPhase("handoff");

        // Requirement 1: preserve the automatic deep-link attempt.
        // On SFSafariViewController this is often ignored; the fallback
        // below covers that case.
        try {
          window.location.href = buildDeepLink(data.ticket);
        } catch {
          /* navigation failure → fallback will show */
        }

        // Requirement 2: if the OS hasn't handed off after the grace
        // period, show the user-tappable fallback link.
        handoffTimer = setTimeout(() => {
          if (requestId.current === myRequest) {
            setShowFallback(true);
          }
        }, HANDOFF_GRACE_MS);
        return;
      }

      // Web flow (unchanged): token + user, backend-validated return_to.
      if (data.token) await setToken(data.token);
      setUser(data.user);

      const returnTo = data.return_to || "/chat";
      const safeReturn =
        typeof returnTo === "string" &&
        returnTo.startsWith("/") &&
        !returnTo.startsWith("//") &&
        !returnTo.includes("://")
          ? returnTo
          : "/chat";

      navigate(safeReturn, { replace: true });
    } catch (e) {
      clearTimeout(timeoutId);
      if (handoffTimer) clearTimeout(handoffTimer);
      // Ignore stale responses.
      if (requestId.current !== myRequest) return;

      const isTimeout =
        (e && e.name === "AbortError") ||
        (e && e.code === "ECONNABORTED") ||
        (e && e.message && e.message.includes("timeout"));
      // Invalidate this request so a late-arriving response can't
      // resurrect it after we've shown an error.
      requestId.current++;

      // Requirement 4: never populate ticket on failure. It stays null,
      // so no ticket-bearing link can render. Error codes carry no
      // credential material (requirement 7).
      setPhase("error");
      setError(isTimeout ? "timeout" : "exchange_failed");
    }
  }, [navigate, setUser]);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    runExchange();

    return () => {
      // Unmount: invalidate any in-flight request so its late response
      // cannot mutate state or trigger navigation.
      requestId.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = useCallback(async () => {
    // 2026-10-11 fix: NEVER reuse the OAuth code. Google authorization
    // codes are single-use, and we stripped code/state from the URL via
    // replaceState at the start of runExchange — so re-running the
    // exchange would read missing params AND reuse a consumed code.
    //
    // Instead, start a completely fresh Google authentication flow.
    // startGoogleLogin() handles platform routing:
    //   - native: fresh PKCE challenge → backend → Browser.open() with
    //     a new Google auth URL (replaces the SFSafariViewController content)
    //   - web: window.location.href to a fresh Google auth URL
    try {
      await startGoogleLogin("/chat");
    } catch {
      // If the fresh flow can't start, stay on the error state.
      // The user can still use "Return to Gully" (native) or navigate manually.
    }
  }, []);

  const errorCopy = {
    missing_params:
      "We couldn't start Google sign-in (missing sign-in details). Please try again.",
    timeout:
      "Google sign-in timed out. Please check your connection and try again.",
    exchange_failed:
      "Google sign-in didn't complete. Your Google code may have already been used — please start again from “Continue with Google”.",
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      data-testid="google-oauth-callback-loader"
    >
      {phase === "loading" && (
        <p className="text-neutral-500 font-display text-lg">Signing you in…</p>
      )}

      {phase === "handoff" && (
        <div className="text-center px-6 max-w-sm">
          <p className="text-neutral-500 font-display text-lg mb-4">
            Signing you in…
          </p>
          {showFallback && ticket && (
            <div data-testid="oauth-return-fallback">
              <p className="text-neutral-400 text-sm mb-4">
                Your sign-in succeeded. Tap below to return to Gully.
              </p>
              {/*
                Requirement 7: the ticket appears ONLY in this href, which
                is required for the OS handoff. It is never logged, never
                sent to analytics, and never copied into error text.
              */}
              <a
                href={buildDeepLink(ticket)}
                data-testid="oauth-return-link"
                className="inline-block px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold text-base"
              >
                Return to Gully
              </a>
            </div>
          )}
        </div>
      )}

      {phase === "error" && (
        <div
          className="text-center px-6 max-w-sm"
          data-testid="oauth-error-state"
        >
          <p className="text-neutral-300 font-display text-lg mb-2">
            Sign-in didn&apos;t complete
          </p>
          <p className="text-neutral-500 text-sm mb-6">
            {errorCopy[error] || errorCopy.exchange_failed}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            data-testid="oauth-retry-button"
            className="inline-block px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold text-base mb-3"
          >
            Try again with Google
          </button>
          {/*
            Native only: if the user doesn't want to retry immediately,
            offer a way back to the app without stranding them in the
            browser. This link carries NO ticket (requirement 4) — it
            simply brings the app to the foreground. The app's
            NativeOAuthListener ignores non-/oauth/callback URLs, so
            this safely lands on the login screen.
          */}
          {isNative() && (
            <div>
              <a
                href="com.gullyapp.mobile://"
                data-testid="oauth-return-to-app-link"
                className="inline-block px-6 py-3 rounded-lg border border-neutral-600 text-neutral-300 font-semibold text-base"
              >
                Return to Gully
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
